import type { GetTileUrlParams } from "./map-tile.type";
import type {
  GM_LatLngBounds,
  GM_LatLngLiteral,
  GM_Point,
  GM_Projection,
} from "../map.type";
import type { LiteralObject } from "@/types";

import { areTilesEnabledAndValids } from "@/config/environmentUtils";

/**
 * Create a LatLngBounds using the legacy order (topRight, bottomLeft).
 * Defensive: ensure coordinates are objects with lat/lng.
 */
function createMapBounds(
  topRight: GM_LatLngLiteral,
  bottomLeft: GM_LatLngLiteral
): GM_LatLngBounds {
  if (
    typeof topRight !== "object" ||
    typeof bottomLeft !== "object" ||
    typeof topRight.lat !== "number" ||
    typeof topRight.lng !== "number" ||
    typeof bottomLeft.lat !== "number" ||
    typeof bottomLeft.lng !== "number"
  ) {
    throw new Error("Invalid coordinates for createMapBounds");
  }
  return new google.maps.LatLngBounds(
    new google.maps.LatLng(topRight.lat, topRight.lng),
    new google.maps.LatLng(bottomLeft.lat, bottomLeft.lng)
  );
}

/**
 * Create the bounds for a specific tile.
 * (max-params: 3)
 */
function createTileBounds(
  proj: GM_Projection,
  coord: GM_Point,
  tileSize: { x: number; y: number }
): GM_LatLngBounds {
  return new google.maps.LatLngBounds(
    proj.fromPointToLatLng(
      new google.maps.Point(coord.x * tileSize.x, (coord.y + 1) * tileSize.y)
    ),
    proj.fromPointToLatLng(
      new google.maps.Point((coord.x + 1) * tileSize.x, coord.y * tileSize.y)
    )
  );
}

/**
 * Compute the wrapped X coordinate for horizontal wrapping.
 */
function getWrappedX(x: number, z2: number): number {
  return x >= 0 ? x : z2 + x;
}

/**
 * Returns true if the tile config is valid for display (same logic as the original inline check).
 */
export function isValidTileConfig(tilesInfo: LiteralObject): boolean {
  return !(
    !tilesInfo ||
    !areTilesEnabledAndValids(tilesInfo) ||
    !tilesInfo?.tilesUrl ||
    !tilesInfo?.tilesSize ||
    !tilesInfo?.tilesMaxZoom ||
    !tilesInfo?.tilesMinZoom ||
    !tilesInfo?.tilesBottomLeftCoordinates ||
    !tilesInfo?.tilesTopRightCoordinates
  );
}

/**
 * Generates the URL for a custom tile, respecting bounds and zoom levels.
 * The bounds are created in the same order as the legacy (topRight, bottomLeft).
 */
export function getTileUrl({
  coord,
  zoom,
  tilesUrl,
  tilesSize,
  tilesMaxZoom,
  tilesMinZoom,
  tilesBottomLeftCoordinates,
  tilesTopRightCoordinates,
  map,
}: GetTileUrlParams): string | null {
  const proj = map.getProjection();
  const z2 = Math.pow(2, zoom);
  const tileSize = { x: Number(tilesSize) / z2, y: Number(tilesSize) / z2 };
  const mapMinZoom = Number(tilesMinZoom);
  const mapMaxZoom = Number(tilesMaxZoom);

  const [topRightLat, topRightLng] = tilesTopRightCoordinates
    .split(",")
    .map(Number);
  const [bottomLeftLat, bottomLeftLng] = tilesBottomLeftCoordinates
    .split(",")
    .map(Number);

  const topRightCoordinates: GM_LatLngLiteral = {
    lat: topRightLat,
    lng: topRightLng,
  };
  const bottomLeftCoordinates: GM_LatLngLiteral = {
    lat: bottomLeftLat,
    lng: bottomLeftLng,
  };

  if (
    !topRightLat ||
    !bottomLeftLat ||
    typeof topRightLat !== "number" ||
    typeof topRightLng !== "number" ||
    typeof bottomLeftLat !== "number" ||
    typeof bottomLeftLng !== "number" ||
    !proj
  ) {
    return null;
  }

  const mapBounds = createMapBounds(topRightCoordinates, bottomLeftCoordinates);
  const tileBounds = createTileBounds(proj, coord, tileSize);
  const x = getWrappedX(coord.x, z2);
  const y = coord.y;

  const inBounds =
    mapBounds.intersects(tileBounds) &&
    mapMinZoom <= zoom &&
    zoom <= mapMaxZoom;

  if (inBounds) {
    return `${tilesUrl}/${zoom}/${x}/${y}.png`;
  }

  return null;
}
